<template>
  <div class="reg-page" style="background: linear-gradient(rgba(74,222,128,0.8), rgba(248,113,113,0.9), rgba(252,211,77,0.7)), url('/assets/images/fake4.png') center/cover no-repeat, #f9fafb;">
    <div class="reg-card">
      <div class="reg-logo">
        <img src="/assets/images/CHECK-APP.png" alt="Check-App" class="logo-img" />
      </div>
      <h1 class="reg-title">{{ $t('registerTitle') }}</h1>
      <p class="reg-subtitle">{{ $t('registerSubtitle') }}</p>

      <form @submit.prevent="handleRegister" class="reg-form" novalidate>
        <div class="form-row">
          <div class="form-group">
            <label>{{ $t('firstName') }}</label>
            <input v-model="form.first_name" type="text" required :placeholder="$t('firstName')" />
          </div>
          <div class="form-group">
            <label>{{ $t('lastName') }}</label>
            <input v-model="form.last_name" type="text" required :placeholder="$t('lastName')" />
          </div>
        </div>
        <div class="form-group">
          <label>{{ $t('email') }}</label>
          <input v-model="form.email" type="email" required :placeholder="$t('enterEmail')" />
        </div>
        <div class="form-group">
          <label>{{ $t('phone') }}</label>
          <input v-model="form.phone" type="tel" :placeholder="$t('enterPhone')" />
        </div>
        <div class="form-group">
          <label>{{ $t('password') }}</label>
          <div class="password-wrap">
            <input v-model="form.password" :type="showPwd ? 'text' : 'password'" required
                   :placeholder="$t('password')" minlength="8" />
            <button type="button" @click="showPwd = !showPwd" class="pwd-toggle">
              <i :class="showPwd ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>
        <div class="form-group">
          <label>{{ $t('confirmPassword') }}</label>
          <input v-model="form.confirm_password" type="password" required
                 :placeholder="$t('confirmPassword')" />
          <span v-if="form.confirm_password && form.password !== form.confirm_password" class="field-error">
            Passwords do not match
          </span>
        </div>

        <div v-if="error" class="alert alert-error">{{ error }}</div>

        <button type="submit" class="btn-primary" :disabled="isLoading || form.password !== form.confirm_password">
          <i v-if="isLoading" class="fas fa-spinner fa-spin mr-2"></i>
          {{ isLoading ? $t('submitting') : $t('createAccount') }}
        </button>
      </form>

      <p class="reg-switch">
        {{ $t('alreadyHaveAccount') }}
        <router-link :to="`/${$i18n.locale}/register/login`">{{ $t('signIn') }}</router-link>
      </p>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useRegistrationStore } from '@/stores/registration';

const store  = useRegistrationStore();
const router = useRouter();
const route  = useRoute();

const form     = reactive({ first_name:'', last_name:'', email:'', phone:'', password:'', confirm_password:'' });
const showPwd  = ref(false);
const error    = ref('');
const isLoading = ref(false);

async function handleRegister() {
  error.value = '';
  if (form.password !== form.confirm_password) return;
  isLoading.value = true;
  try {
    const { first_name, last_name, email, phone, password } = form;
    await store.register({ first_name, last_name, email, phone, password });
    router.push(`/${route.params.lang}/register/verify-email`);
  } catch (e) {
    error.value = e.message;
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped>
.reg-page { min-height: 100vh; display: flex; align-items: center; justify-content: center;
   padding: 20px; }
.reg-card { background: #fff; border-radius: 16px; box-shadow: 0 8px 32px rgba(0,0,0,.1);
  padding: 40px; width: 100%; max-width: 520px; }
.reg-logo { display: flex; justify-content: center; align-items: center; margin-bottom: 20px; }
.logo-img { height: 20px; display: block; margin: 0 auto; }
.reg-title { font-size: 1.6rem; font-weight: 700; color: #1a3a5c; text-align: center; margin-bottom: 6px; }
.reg-subtitle { color: #6b7280; text-align: center; margin-bottom: 28px; font-size: .95rem; }
.reg-form { display: flex; flex-direction: column; gap: 16px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.form-group { display: flex; flex-direction: column; gap: 5px; }
.form-group label { font-size: .875rem; font-weight: 600; color: #374151; }
.form-group input { border: 1.5px solid #d1d5db; border-radius: 8px; padding: 10px 14px;
  font-size: .9rem; transition: border-color .2s; }
.form-group input:focus { outline: none; border-color: #1a3a5c; }
.password-wrap { position: relative; }
.password-wrap input { width: 100%; padding-right: 40px; box-sizing: border-box; }
.pwd-toggle { position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: #6b7280; }
.field-error { color: #ef4444; font-size: .8rem; }
.alert-error { background: #fef2f2; border: 1px solid #fca5a5; color: #b91c1c;
  border-radius: 8px; padding: 10px 14px; font-size: .875rem; }
.btn-primary { background: #1a3a5c; color: #fff; border: none; border-radius: 10px;
  padding: 13px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: background .2s;
  display: flex; align-items: center; justify-content: center; }
.btn-primary:hover:not(:disabled) { background: #2d6a9f; }
.btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.reg-switch { text-align: center; margin-top: 20px; font-size: .9rem; color: #6b7280; }
.reg-switch a { color: #1a3a5c; font-weight: 600; text-decoration: none; }
@media (max-width: 480px) { .form-row { grid-template-columns: 1fr; } }
</style>